<template>
    <div class="radar-chart">
        <h5 v-if="tableName">
            {{ tableName }}
        </h5>

        <span class="sub-header" v-if="subHeader">
			{{ subHeader }}
		</span>

        <span v-if="typeof dataSet === 'undefined'">No record found</span>

        <section class="chart-legend" v-else>
            <div class="legend-item" v-if="dataSet.totalSession.length > 0">
                <span class="legend-icon overall" />

                Overall User
            </div>

            <div class="legend-item" v-if="dataSet.user.length > 0">
                <span class="legend-icon user" />

                User Emotion
            </div>
        </section>

        <section class="chart-container">
            <canvas :id="uniqueId" />
        </section>
    </div>
</template>

<script>
import Chart from 'chart.js'
export default {
	props: {
		tableName: {
			type: String,
			default: ''
		},

		subHeader: {
			type: String,
			default: ''
		},

		dataSet: {
			type: Object,
			default: () => {}
		},

		emotionLevels: {
			type: Array,
			default: () => []
		}
	},

	data () {
		return {
			uniqueId: ''
		}
	},

	created () {
		this.generateUniqueId()
	},

	mounted () {
		if (typeof this.dataSet !== 'undefined') this.setUpRadarChart()
	},

	methods: {
		/* Created Methods */
		generateUniqueId () {
			this.uniqueId = Math.random().toString(36).substr(2, 9)
		},

		/* Mounted Methods */
		setUpRadarChart () {
			const radarChartCanvas = document.getElementById(this.uniqueId)

			const data = {
				labels: this.setLabels(),

				datasets: this.setDataSets()
			}

			const options = {
				responsive: true,

				animation: {
					duration: 0 // general animation time
				},

				hover: {
					animationDuration: 0 // duration of animations when hovering an item
				},

				responsiveAnimationDuration: 0, // animation duration after a resize

				scale: this.setScale(),

				tooltips: {
					enabled: false
				},

				legend: {
					display: false
				}
			}

			// eslint-disable-next-line
			const radarChart = new Chart(
				radarChartCanvas,
				{
					type: 'radar',
					data: data,
					options: options
				}
			)
		},

		/* setLabels Methods */
		setLabels () {
			return this.emotionLevels.reduce((total, item, index) => {
				total.push([
					item,
					this.setLabelWithPercentage(index)
				])

				return total
			}, [])
		},

		setLabelWithPercentage (index) {
			if (typeof this.dataSet !== 'undefined') {
				const overallTotal = this.dataSet.totalSession.reduce((a, b) => a + b, 0)
				const overallSelected = this.dataSet.totalSession[index]
				const overallPercentage = `${((overallSelected / overallTotal) * 100).toFixed(0)}%`

				const userTotal = this.dataSet.user.reduce((a, b) => a + b, 0)
				const userSelected = this.dataSet.user[index]
				const userPercentage = `${((userSelected / userTotal) * 100).toFixed(0)}%`

				const overall = `Overall (${overallPercentage})`
				const user = this.dataSet.user.length > 0 ? `User (${userPercentage})` : ''

				let label = ''

				if (this.dataSet.totalSession.length > 0) label = overall
				label += user
				return label
				// return overall + ' ' + user
			}

			return ''
		},

		/* setDataSets Methods */
		setDataSets () {
			let arr = [{
				label: 'User Emotion',
				data: this.dataSet.user,

				borderWidth: 1.5,
				backgroundColor: [
					'rgba(59, 134, 255, 0.6)'
				],
				borderColor: [
					'rgba(59, 134, 255, 1)'
				],

				pointBackgroundColor: '#fff',
				pointBorderWidth: 1.5,
				pointBorderColor: [
					'rgba(59, 134, 255, 1)'
				]
			}]

			if (this.dataSet.totalSession.length > 0) {
				arr.push({
					label: 'Overall User',
					data: this.dataSet.totalSession,

					borderWidth: 1.5,
					backgroundColor: [
						'rgba(215, 215, 252, 0.5)'
					],
					borderColor: [
						'rgba(215, 215, 252, 1)'
					],

					pointBackgroundColor: '#fff',
					pointBorderWidth: 2,
					pointBorderColor: [
						'rgba(215, 215, 252, 1)'
					]
				})
			}

			return arr
		},

		/* setScale Methods */
		setScale () {
			const isMaxEmotionOnlyOne = () => {
				const overallTotal = Math.max(...this.dataSet.totalSession)
				const userTotal = Math.max(...this.dataSet.user)

				return overallTotal === 1 && userTotal === 1
			}

			return {
				ticks: {
					display: true,
					beginAtZero: true,
					fontSize: 11,
					stepSize: isMaxEmotionOnlyOne() ? 0.2 : undefined,
					fontColor: '#999'
				},

				pointLabels: {
					fontSize: 11,
					fontColor: '#777'
				}
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.radar-chart {
    position: relative;

	&:not(:last-child) {
		padding-bottom: 25px;
	}

    h4 {
		font-size: 16px;
		padding: 0px;
	}

    .sub-header {
		display: block;
		font-size: 14px;
		color: #5e5e5e;
		padding: 5px 0 0 0;
	}

    .chart-legend {
        position: absolute;
        top: 70px;
        left: 0;

        .legend-item {
            display: flex;
            align-items: center;
            font-size: 13px;

            &:first-child {
                margin-bottom: 5px;
            }

            .legend-icon {
                display: inline-block;
                width: 23px;
                height: 10px;
                border-radius: 10px;
                margin-right: 10px;
                flex-shrink: 0;

                &.overall {
                    background: #d7d7fc;
                }

                &.user {
                    background: #3b86ff;
                }
            }
        }
    }

    .chart-container {
        width: 700px;
        margin: 0 auto;
    }
}
</style>
